import React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

export const THEME = {
  primary: "#d86a78",
  light: "#fff6f7",
  border: "#f2c6cc",
};

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function getErrorMessage(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  return error?.response?.data?.message || error?.data?.message || error?.message || "Something went wrong";
}

export function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
}

export function idOf(row) {
  return row?.id || row?._id || row?.restaurantId || row?.restaurant_id || row?.employeeId || row?.employee_id || row?.orderId || row?.order_id || "";
}

export function restaurantIdOf(row) {
  return row?.restaurantId || row?.restaurant_id || row?.id || row?._id || "";
}

export function employeeIdOf(row) {
  return row?.employeeId || row?.employee_id || row?.id || row?._id || "";
}

export function PageHeader({ eyebrow, title, subtitle, actions, icon }) {
  return (
    <div className="mb-6 rounded-[22px] border border-[#f2c6cc] bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#fff6f7] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-[#d86a78]">
              {icon || null}
              {eyebrow}
            </div>
          ) : null}
          <h1 className="text-[26px] font-bold leading-tight text-[#d86a78] sm:text-[24px]">{title}</h1>
          {subtitle ? <p className="mt-1 max-w-4xl text-[13px] leading-relaxed text-slate-500">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

export function Button({ children, variant = "primary", size = "md", className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-[12px] font-bold transition disabled:cursor-not-allowed disabled:opacity-60";
  const sizes = size === "sm" ? "px-3 py-1.5 text-[12px]" : "px-4 py-2 text-[13px]";
  const variants = {
    primary: "bg-[#d86a78] text-white hover:bg-[#c95b69]",
    ghost: "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]",
    soft: "bg-[#fff6f7] text-[#d86a78] hover:bg-[#ffecef]",
    orange: "bg-[#ef9440] text-white hover:bg-[#dd8130]",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
    dark: "bg-slate-900 text-white hover:bg-[#d86a78]",
  };
  return (
    <button type="button" className={cn(base, sizes, variants[variant] || variants.primary, className)} {...props}>
      {children}
    </button>
  );
}

export function Card({ children, className = "" }) {
  return <div className={cn("rounded-[20px] border border-[#f2c6cc] bg-white p-5 shadow-sm", className)}>{children}</div>;
}

export function StatCard({ icon, label, value, hint, tone = "pink", trend }) {
  const toneClass = tone === "orange" ? "bg-orange-50 text-[#ef9440]" : tone === "green" ? "bg-emerald-50 text-emerald-600" : tone === "blue" ? "bg-sky-50 text-sky-600" : "bg-[#fff0f2] text-[#d86a78]";
  return (
    <Card className="min-h-[142px] overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className={cn("flex h-11 w-11 items-center justify-center rounded-[14px]", toneClass)}>{icon}</div>
        <div className="text-right">
          <div className="text-[25px] font-extrabold text-slate-950">{value ?? 0}</div>
          <div className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500">{label}</div>
          {hint ? <div className="mt-1 text-[11px] font-semibold text-emerald-600">{hint}</div> : null}
        </div>
      </div>
      {trend ? <Sparkline values={trend} /> : <Sparkline values={[2, 4, 3, 7, 5, 8, 9]} />}
    </Card>
  );
}

export function Sparkline({ values = [] }) {
  const nums = values.map((v) => Number(v || 0));
  const max = Math.max(...nums, 1);
  const pts = nums.map((v, i) => `${(i / Math.max(nums.length - 1, 1)) * 100},${40 - (v / max) * 28 + 4}`).join(" ");
  return (
    <svg viewBox="0 0 100 48" className="mt-5 h-10 w-full overflow-visible">
      <polyline points={pts} fill="none" stroke="#d86a78" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Input({ label, className = "", ...props }) {
  return (
    <label className="block">
      {label ? <span className="mb-1 block text-[12px] font-bold text-slate-500">{label}</span> : null}
      <input className={cn("h-11 w-full rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[13px] outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]", className)} {...props} />
    </label>
  );
}

export function Select({ label, children, className = "", ...props }) {
  return (
    <label className="block">
      {label ? <span className="mb-1 block text-[12px] font-bold text-slate-500">{label}</span> : null}
      <select className={cn("h-11 w-full rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[13px] outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]", className)} {...props}>
        {children}
      </select>
    </label>
  );
}

export function Textarea({ label, className = "", ...props }) {
  return (
    <label className="block">
      {label ? <span className="mb-1 block text-[12px] font-bold text-slate-500">{label}</span> : null}
      <textarea className={cn("min-h-[110px] w-full rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-3 text-[13px] outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]", className)} {...props} />
    </label>
  );
}

export function Switch({ checked, onChange, disabled = false }) {
  return (
    <button type="button" disabled={disabled} role="switch" aria-checked={checked} onClick={() => onChange?.(!checked)} className={cn("relative h-[24px] w-[50px] rounded-full transition", checked ? "bg-[#d86a78]" : "bg-slate-300", disabled ? "cursor-not-allowed opacity-60" : "")}>
      <span className={cn("absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition", checked ? "left-[29px]" : "left-[3px]")} />
    </button>
  );
}

export function StatusPill({ value }) {
  const raw = String(value ?? "").trim();
  const s = raw.toLowerCase();
  const ok = ["active", "approved", "paid", "delivered", "open", "true", "auto_accepted", "placed", "completed"].includes(s);
  const warn = ["pending", "payment_pending", "driver_assigning", "waiting", "processing"].includes(s);
  const cls = ok ? "bg-emerald-50 text-emerald-700" : warn ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700";
  return <span className={cn("inline-flex max-w-full break-all rounded-full px-3 py-1 text-left text-[11px] font-bold uppercase leading-tight", cls)}>{raw || "-"}</span>;
}

export function LoadingBox({ label = "Loading..." }) {
  return (
    <div className="flex min-h-[260px] items-center justify-center rounded-[20px] border border-[#f2c6cc] bg-white text-slate-500">
      <Loader2 className="mr-2 animate-spin" size={18} /> {label}
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  const message = getErrorMessage(error);
  if (!message) return null;
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-[16px] border border-rose-200 bg-rose-50 p-4 text-rose-700 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-[13px] font-semibold"><AlertTriangle size={17} /> {message}</div>
      {onRetry ? <Button variant="ghost" onClick={onRetry}>Retry</Button> : null}
    </div>
  );
}

export function EmptyState({ title = "No data found", subtitle = "Try changing filters or add new data.", icon }) {
  return (
    <div className="rounded-[18px] border border-dashed border-[#f2c6cc] bg-[#fffafb] p-8 text-center">
      {icon ? <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#fff0f2] text-[#d86a78]">{icon}</div> : null}
      <div className="text-[16px] font-extrabold text-[#d86a78]">{title}</div>
      <div className="mt-1 text-[13px] text-slate-500">{subtitle}</div>
    </div>
  );
}

export function TableShell({ columns = [], children, minWidth = 900 }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-left text-[13px]" style={{ minWidth }}>
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wide text-slate-400">
            {columns.map((col) => <th key={col} className="px-4 py-3">{col}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
